import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowDown } from 'lucide-react';
import './AIScrollButton.css';
import * as React from 'react';
import { Button } from '@/components/atoms/buttons/Button';
import { cn } from '@/lib/utils';

// =============================================================================
// Variants
// =============================================================================

const scrollButtonVariants = cva(
  'fixed flex items-center justify-center shadow-lg',
  {
    variants: {
      variant: {
        default: 'bg-background hover:bg-muted border border-border',
        primary: 'bg-primary/10 hover:bg-primary/90 text-primary-foreground',
        ghost:
          'bg-card/80 hover:bg-background glass-surface [--glass-blur:8px] border border-border',
      },
      size: {
        sm: 'size-8 rounded-full',
        md: 'size-10 rounded-full',
        lg: 'size-12 rounded-xl',
      },
      position: {
        'bottom-center':
          'bottom-4 start-1/2 -translate-x-1/2 rtl:translate-x-1/2',
        'bottom-right': 'bottom-4 end-4',
        'bottom-left': 'bottom-4 start-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      position: 'bottom-center',
    },
  },
);

// =============================================================================
// useScrollToBottom Hook
// =============================================================================

export interface UseScrollToBottomOptions {
  /** Threshold in pixels from bottom to consider "at bottom" */
  threshold?: number;
  /** Scroll behavior */
  behavior?: ScrollBehavior;
  /** Enable auto-scroll mode: tracks whether auto-scroll should fire on content changes */
  autoScroll?: boolean;
}

export interface UseScrollToBottomReturn {
  /** Whether the user is at the bottom of the container */
  isAtBottom: boolean;
  /** Scroll to bottom function */
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  /** Ref to attach to the scrollable container */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Ref to attach to the scroll anchor element */
  anchorRef: React.RefObject<HTMLDivElement | null>;
  /** Whether auto-scroll should fire (only meaningful when autoScroll option is true) */
  shouldAutoScroll: boolean;
  /** Call when user scrolls manually to disable auto-scroll (re-enables when user scrolls back to bottom) */
  handleUserScroll: () => void;
  /** Trigger an auto-scroll if shouldAutoScroll is true (uses requestAnimationFrame) */
  triggerAutoScroll: () => void;
}

// `auto` must also override consumer CSS scroll-smooth.
function scrollContainer(
  container: HTMLElement,
  top: number,
  behavior: ScrollBehavior,
) {
  const previous = container.style.scrollBehavior;
  if (behavior === 'auto' || behavior === 'instant')
    container.style.scrollBehavior = 'auto';
  container.scrollTo({ top, behavior });
  container.style.scrollBehavior = previous;
}

function nearestScrollContainer(anchor: HTMLElement | null | undefined) {
  const view = anchor?.ownerDocument.defaultView;
  for (
    let parent = anchor?.parentElement;
    parent && view;
    parent = parent.parentElement
  ) {
    if (/(auto|scroll)/.test(view.getComputedStyle(parent).overflowY))
      return parent;
  }
  return null;
}

function scrollLatest(
  container: HTMLElement | null | undefined,
  anchor: HTMLElement | null | undefined,
  behavior: ScrollBehavior,
) {
  container ??= nearestScrollContainer(anchor);
  const view = (container ?? anchor)?.ownerDocument.defaultView;
  const resolved = view?.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : behavior;
  if (container) scrollContainer(container, container.scrollHeight, resolved);
  else
    anchor?.scrollIntoView({
      behavior: resolved,
      block: 'end',
      inline: 'nearest',
    });
}

// Default buttons reuse the hook controller for their resolved scroll target.
const targetNavigation = new WeakMap<
  HTMLElement,
  (behavior?: ScrollBehavior) => void
>();

// Both public activation paths use the same native navigation lifecycle.
function useScrollNavigation(
  containerRef?: React.RefObject<HTMLElement | null>,
  anchorRef?: React.RefObject<HTMLElement | null>,
  onInterrupt?: () => void,
) {
  const navigating = React.useRef(false);
  const navigate = React.useCallback(
    (behavior: ScrollBehavior) => {
      const container =
        containerRef?.current ?? nearestScrollContainer(anchorRef?.current);
      navigating.current = true;
      scrollLatest(container, anchorRef?.current, behavior);
      if (
        container &&
        container.scrollHeight - container.clientHeight - container.scrollTop <=
          1
      )
        navigating.current = false;
    },
    [containerRef, anchorRef],
  );

  React.useEffect(() => {
    const anchor = anchorRef?.current;
    const container = containerRef?.current ?? nearestScrollContainer(anchor);
    const view = (container ?? anchor)?.ownerDocument.defaultView;
    if (!view) return;
    const media = view.matchMedia('(prefers-reduced-motion: reduce)');
    const preferenceChanged = () => {
      if (media.matches && navigating.current) {
        navigating.current = false;
        scrollLatest(container, anchor, 'auto');
      }
    };
    const settled = () => {
      if (
        container &&
        container.scrollHeight - container.clientHeight - container.scrollTop <=
          1
      )
        navigating.current = false;
    };
    const stop = () => {
      const active = navigating.current;
      navigating.current = false;
      if (container && active)
        scrollContainer(container, container.scrollTop, 'auto');
    };
    const interrupt = () => {
      stop();
      onInterrupt?.();
    };
    const key = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('input, textarea, [contenteditable="true"], button'))
        return;
      if (
        [
          'ArrowUp',
          'ArrowDown',
          'PageUp',
          'PageDown',
          'Home',
          'End',
          ' ',
        ].includes(event.key)
      )
        interrupt();
    };
    media.addEventListener('change', preferenceChanged);
    container?.addEventListener('scroll', settled, { passive: true });
    container?.addEventListener('wheel', interrupt, { passive: true });
    container?.addEventListener('touchstart', interrupt, { passive: true });
    container?.addEventListener('keydown', key);
    return () => {
      stop();
      media.removeEventListener('change', preferenceChanged);
      container?.removeEventListener('scroll', settled);
      container?.removeEventListener('wheel', interrupt);
      container?.removeEventListener('touchstart', interrupt);
      container?.removeEventListener('keydown', key);
    };
  }, [containerRef, anchorRef, onInterrupt]);

  return { navigate, navigating };
}

export function useScrollToBottom({
  threshold = 50,
  behavior = 'smooth',
  autoScroll: autoScrollEnabled = false,
}: UseScrollToBottomOptions = {}): UseScrollToBottomReturn {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = React.useState(true);
  const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);

  const pending = React.useRef<{ view: Window; id: number } | null>(null);
  const following = React.useRef(true);
  const lastScrollTop = React.useRef(0);
  const cancelFrame = React.useCallback(() => {
    if (pending.current)
      pending.current.view.cancelAnimationFrame(pending.current.id);
    pending.current = null;
  }, []);
  const interrupt = React.useCallback(() => {
    cancelFrame();
    following.current = false;
    setShouldAutoScroll(false);
  }, [cancelFrame]);
  const { navigate, navigating } = useScrollNavigation(
    containerRef,
    anchorRef,
    interrupt,
  );
  React.useEffect(() => cancelFrame, [cancelFrame]);
  const scrollToBottom = React.useCallback(
    (overrideBehavior?: ScrollBehavior) => {
      cancelFrame();
      following.current = true;
      setShouldAutoScroll(true);
      navigate(overrideBehavior ?? behavior);
    },
    [behavior, cancelFrame, navigate],
  );

  React.useEffect(() => {
    const container =
      containerRef.current ?? nearestScrollContainer(anchorRef.current);
    if (!container) return;
    targetNavigation.set(container, scrollToBottom);
    return () => {
      if (targetNavigation.get(container) === scrollToBottom)
        targetNavigation.delete(container);
    };
  }, [scrollToBottom]);

  const checkIsAtBottom = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return true;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom <= threshold;
  }, [threshold]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atBottom = checkIsAtBottom();
      lastScrollTop.current = container.scrollTop;
      setIsAtBottom((prev) => (prev === atBottom ? prev : atBottom));
      if (autoScrollEnabled && atBottom) {
        following.current = true;
        setShouldAutoScroll(true);
      }
    };

    handleScroll();

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [checkIsAtBottom, autoScrollEnabled]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const OwnerResizeObserver =
      container.ownerDocument.defaultView?.ResizeObserver;
    if (!OwnerResizeObserver) return;

    const resizeObserver = new OwnerResizeObserver(() => {
      const atBottom = checkIsAtBottom();
      setIsAtBottom((prev) => (prev === atBottom ? prev : atBottom));
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [checkIsAtBottom]);

  const handleUserScroll = React.useCallback(() => {
    // A delayed scroll event from the previous bottom can arrive after content grows.
    if (
      pending.current &&
      containerRef.current?.scrollTop === lastScrollTop.current
    )
      return;
    if (!navigating.current && !checkIsAtBottom()) {
      cancelFrame();
      following.current = false;
      setShouldAutoScroll(false);
    }
  }, [checkIsAtBottom, cancelFrame, navigating]);

  const triggerAutoScroll = React.useCallback(() => {
    if (!autoScrollEnabled || !following.current || pending.current) return;
    const view = (containerRef.current ?? anchorRef.current)?.ownerDocument
      .defaultView;
    if (!view) return;
    pending.current = {
      view,
      id: view.requestAnimationFrame(() => {
        pending.current = null;
        if (following.current)
          scrollLatest(containerRef.current, anchorRef.current, 'auto');
      }),
    };
  }, [autoScrollEnabled]);

  return {
    isAtBottom,
    scrollToBottom,
    containerRef,
    anchorRef,
    shouldAutoScroll,
    handleUserScroll,
    triggerAutoScroll,
  };
}

// =============================================================================
// Main AIScrollButton Component
// =============================================================================

export interface AIScrollButtonProps
  extends VariantProps<typeof scrollButtonVariants> {
  /** Target scroll element ref */
  scrollRef?: React.RefObject<HTMLElement | null>;
  /** Scrollable container ref */
  containerRef?: React.RefObject<HTMLElement | null>;
  /** Threshold in pixels from bottom to show button */
  threshold?: number;
  /** Click handler (defaults to scrolling to bottom) */
  onClick?: () => void;
  /** Whether to show the button (controlled) */
  visible?: boolean;
  /** Label for accessibility */
  label?: string;
  /** Show unread count badge */
  unreadCount?: number;
  /** Additional class name */
  className?: string;
}

function getIconSize(size: AIScrollButtonProps['size']): string {
  switch (size) {
    case 'sm':
      return 'size-4';
    case 'lg':
      return 'size-6';
    default:
      return 'size-5';
  }
}

function AIScrollButton({
  scrollRef,
  containerRef,
  threshold = 50,
  onClick,
  visible: controlledVisible,
  variant,
  size,
  position,
  label = 'Scroll to bottom',
  unreadCount,
  className,
}: AIScrollButtonProps) {
  const [internalVisible, setInternalVisible] = React.useState(false);
  const { navigate } = useScrollNavigation(containerRef, scrollRef);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const preserveFocus = React.useCallback(
    (node: HTMLDivElement | null) => {
      const previous = rootRef.current;
      if (!node && previous?.contains(previous.ownerDocument.activeElement)) {
        const container =
          containerRef?.current ?? nearestScrollContainer(scrollRef?.current);
        if (container) {
          const tabIndex = container.getAttribute('tabindex');
          container.setAttribute('tabindex', '-1');
          container.focus({ preventScroll: true });
          if (tabIndex === null) container.removeAttribute('tabindex');
          else container.setAttribute('tabindex', tabIndex);
        }
      }
      rootRef.current = node;
    },
    [containerRef, scrollRef],
  );

  // Track scroll position if containerRef is provided and no controlled visibility
  React.useEffect(() => {
    if (controlledVisible !== undefined) return;

    const container = containerRef?.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setInternalVisible(distanceFromBottom > threshold);
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [containerRef, threshold, controlledVisible]);

  const isVisible = controlledVisible ?? internalVisible;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      const container =
        containerRef?.current ?? nearestScrollContainer(scrollRef?.current);
      const sharedNavigate = container && targetNavigation.get(container);
      (sharedNavigate || navigate)('smooth');
    }
  };

  const iconSize = getIconSize(size);

  return (
    <>
      {isVisible && (
        <div
          ref={preserveFocus}
          data-slot="ai-scroll-button"
          className={cn(
            scrollButtonVariants({ variant, size, position }),
            'ai-scroll-button-arrival',
            className,
          )}
        >
          <Button
            appearance="ghost"
            variant="secondary"
            size="icon"
            onClick={handleClick}
            className="size-full rounded-full"
            aria-label={label}
          >
            <ArrowDown
              className={cn(
                iconSize,
                variant === 'primary'
                  ? 'text-primary-foreground'
                  : 'text-muted-foreground',
              )}
            />
          </Button>

          {/* Unread count badge */}
          {unreadCount !== undefined && unreadCount > 0 && (
            <div
              role="status"
              className="absolute -top-1 -end-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-700 text-destructive-foreground text-xs font-medium motion-reduce:transition-none"
              aria-label={`${unreadCount > 99 ? '99+' : unreadCount} new messages`}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </div>
          )}
        </div>
      )}
    </>
  );
}

AIScrollButton.displayName = 'AIScrollButton';

// =============================================================================
// Scroll Anchor Component
// =============================================================================

export interface AIScrollAnchorProps {
  /** Ref to expose for scrolling */
  ref?: React.Ref<HTMLDivElement>;
  /** Additional class name */
  className?: string;
}

const AIScrollAnchor = React.forwardRef<HTMLDivElement, AIScrollAnchorProps>(
  ({ className }, ref) => {
    return (
      <div ref={ref} className={cn('h-px w-full', className)} aria-hidden />
    );
  },
);

AIScrollAnchor.displayName = 'AIScrollAnchor';

export { AIScrollAnchor, AIScrollButton, scrollButtonVariants };
